/**
 * CourtageScreen — the designed Courtage area (Story UI-U5).
 *
 * Makes the fully-built-and-tested Epic-11 backend (11.1–11.8 + HC1/HC2) VISIBLE
 * (gap G6 "Courtage — zero UI"). A UI-U1 SegmentedControl switches four scope
 * slices: Abrechnungen (statement list → StatementPeek) · Akonto (running Salden) ·
 * Co-Broker (split rows) · Monitoring (reconciliation Kennzahlen + Erwartungen).
 *
 * Renders SERVER decisions only (design.md §2): status = dot + text (never a filled
 * pill), soft badges for category/flags, Finance-gated CHF → `—`. A 403 on the
 * statements list → an honest Kein-Zugriff state (the server decides visibility).
 *
 * [Source: brokly-design-reference/components/brokly/courtage-view.tsx] (ported list patterns)
 * [Source: apps/web/src/features/invoices/InvoicesScreen.tsx] (list + peek + keyboard shape)
 * [Source: docs/stories/ui-u5-courtage-tarifmatrix.md#Tasks — Task 6]
 */

import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { Coins, Search } from 'lucide-react';
import { DataTable } from '../../components/data/DataTable';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { StickyPageHeader } from '../../components/ui/StickyPageHeader';
import { FilterChip } from '../../components/ui/FilterChip';
import { StatusDot } from '../../components/ui/StatusDot';
import { Badge } from '../../components/ui/Badge';
import { EntityLink } from '../../components/ui/EntityLink';
import { Card } from '../../components/ui/Card';
import { TableCell } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { dateShortDe } from '../../lib/format';
import { COMMISSION_EXPECTATION_STATUS_LABELS } from '@brokly/shared';
import type {
  AkontoBalanceViewDto,
  CommissionExpectationViewDto,
  CommissionSplitViewDto,
  CommissionStatementListItemDto,
  ReconciliationKennzahlenDto,
} from '@brokly/shared';
import {
  AKONTO_STATUS_META,
  chfOrDash,
  commissionKindLabel,
  CourtageAccessError,
  currentMonth,
  diffToneClass,
  EXPECTATION_STATUS_META,
  monthBounds,
  signedChfOrDash,
  SPLIT_STATUS_META,
  statementStatus,
} from './api/courtage-api';
import {
  useCourtageAkonto,
  useCourtageExpectations,
  useCourtageKennzahlen,
  useCourtageSplits,
  useCourtageStatements,
} from './hooks/useCourtage';
import { StatementPeek } from './StatementPeek';

type Slice = 'abrechnungen' | 'akonto' | 'cobroker' | 'monitoring';

const num = 'text-right mono-num tabular-nums';

export function CourtageScreen() {
  const [slice, setSlice] = useState<Slice>('abrechnungen');
  const statements = useCourtageStatements();

  return (
    <div className="px-6 py-5">
      {/* E3: pinned so the Bereich switch stays reachable while a slice scrolls. */}
      <StickyPageHeader className="-mx-6 -mt-5 px-6 pt-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="display-title">Courtage</h1>
        <SegmentedControl
          ariaLabel="Courtage-Bereich"
          value={slice}
          onValueChange={(v) => setSlice(v as Slice)}
          options={[
            { value: 'abrechnungen', label: 'Abrechnungen' },
            { value: 'akonto', label: 'Akonto' },
            { value: 'cobroker', label: 'Co-Broker' },
            { value: 'monitoring', label: 'Monitoring' },
          ]}
        />
      </div>
      </StickyPageHeader>

      {slice === 'abrechnungen' && <AbrechnungenSlice statements={statements} />}
      {slice === 'akonto' && <AkontoSlice />}
      {slice === 'cobroker' && <CoBrokerSlice />}
      {slice === 'monitoring' && <MonitoringSlice />}
    </div>
  );
}

// ── Abrechnungen slice ───────────────────────────────────────────────────────

const VIEWS: { key: string; label: string; match: (s: CommissionStatementListItemDto) => boolean }[] = [
  { key: 'alle', label: 'Alle', match: () => true },
  { key: 'entscheid', label: 'Braucht Entscheid', match: (s) => s.braucht_entscheid },
  { key: 'auto', label: 'Automatisch abgeglichen', match: (s) => !s.braucht_entscheid },
];

function sumCol(rows: CommissionStatementListItemDto[], key: 'soll_total' | 'ist_total' | 'delta_total'): string | null {
  if (rows.length === 0) return '0.00';
  let acc = 0;
  for (const r of rows) {
    const v = r[key];
    if (v === null) return null; // Finance-gated → no client sum
    acc += Number(v);
  }
  return acc.toFixed(2);
}

function AbrechnungenSlice({
  statements,
}: {
  statements: ReturnType<typeof useCourtageStatements>;
}) {
  const navigate = useNavigate();
  const { data = [], isLoading, isError, error } = statements;
  const [activeView, setActiveView] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Decision feedback (AC3: mutations toast on success/error) — the InvoicesScreen
  // pattern. Without this the Entscheid-bar transitions/override would fail silently.
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  function showFeedback(msg: string) {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 2500);
  }
  function showError(msg: string) {
    setErrorFeedback(msg);
    window.setTimeout(() => setErrorFeedback(null), 3500);
  }

  const visible = useMemo(() => {
    const view = VIEWS[activeView] ?? VIEWS[0]!;
    const q = search.trim().toLowerCase();
    return data.filter((s) => {
      if (!view.match(s)) return false;
      if (!q) return true;
      return `${s.statement_id} ${s.carrier_name ?? ''} ${s.period_from ?? ''} ${s.period_to ?? ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [data, activeView, search]);

  const selectedIndex = visible.findIndex((s) => s.statement_id === selectedId);
  const selected = selectedIndex >= 0 ? visible[selectedIndex]! : null;

  // Keyboard: J/K navigate, Esc closes (suppressed while typing) — DecisionPeek convention.
  useEffect(() => {
    if (selectedId === null) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') setSelectedId(null);
      else if (e.key === 'j' || e.key === 'J') gotoNext();
      else if (e.key === 'k' || e.key === 'K') gotoPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, visible]);

  function gotoNext() {
    if (selectedIndex >= 0 && selectedIndex < visible.length - 1) setSelectedId(visible[selectedIndex + 1]!.statement_id);
  }
  function gotoPrev() {
    if (selectedIndex > 0) setSelectedId(visible[selectedIndex - 1]!.statement_id);
  }

  const columns = useMemo<ColumnDef<CommissionStatementListItemDto>[]>(
    () => [
      {
        accessorKey: 'statement_id',
        header: 'Abrechnungs-Nr.',
        cell: ({ row }) => <span className="mono-num font-medium">{row.original.statement_id.slice(0, 8).toUpperCase()}</span>,
      },
      {
        accessorKey: 'carrier_name',
        header: 'Versicherer',
        cell: ({ row }) =>
          row.original.carrier_uid ? (
            <EntityLink
              onActivate={() => void navigate({ to: '/versicherer/$id', params: { id: row.original.carrier_uid! } })}
            >
              {row.original.carrier_name ?? row.original.carrier_uid}
            </EntityLink>
          ) : (
            <span>{row.original.carrier_name ?? '-'}</span>
          ),
      },
      {
        id: 'periode',
        header: 'Periode',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {dateShortDe(row.original.period_from)} - {dateShortDe(row.original.period_to)}
          </span>
        ),
      },
      {
        accessorKey: 'line_count',
        header: 'Zeilen',
        cell: ({ row }) => <span className="tabular-nums text-muted-foreground">{row.original.line_count}</span>,
      },
      { accessorKey: 'soll_total', header: 'Soll', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.soll_total)}</div> },
      { accessorKey: 'ist_total', header: 'Ist', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.ist_total)}</div> },
      {
        accessorKey: 'delta_total',
        header: 'Differenz',
        cell: ({ row }) => (
          <div className={cn(num, 'font-medium', diffToneClass(row.original.delta_total))}>
            {signedChfOrDash(row.original.delta_total)}
          </div>
        ),
      },
      {
        id: 'offen',
        header: 'Offen',
        cell: ({ row }) => (
          <span className="tabular-nums text-muted-foreground">
            {row.original.open_line_count}/{row.original.line_count}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const st = statementStatus(row.original.status);
          return <StatusDot tone={st.tone} label={st.label} />;
        },
      },
    ],
    [navigate],
  );

  if (isError && error instanceof CourtageAccessError) {
    return <KeinZugriff />;
  }

  const views = VIEWS.map((v) => ({ label: v.label, count: data.filter(v.match).length }));
  const sumSoll = sumCol(visible, 'soll_total');
  const sumIst = sumCol(visible, 'ist_total');
  const sumDelta = sumCol(visible, 'delta_total');

  return (
    <>
      {/* view pills */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {views.map((v, i) => (
          <FilterChip key={v.label} label={v.label} count={v.count} active={activeView === i} onClick={() => setActiveView(i)} />
        ))}
      </div>

      {/* search */}
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-9 max-w-sm flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm">
          <Search className="size-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nr., Versicherer, Periode …"
            className="flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground tabular-nums">
          {visible.length} von {data.length}
        </span>
      </div>

      {isLoading ? (
        <EmptyCard>Lade Courtageabrechnungen …</EmptyCard>
      ) : isError ? (
        <EmptyCard role="alert">Courtageabrechnungen konnten nicht geladen werden.</EmptyCard>
      ) : visible.length === 0 ? (
        <EmptyCard>Noch keine Courtageabrechnungen eingegangen.</EmptyCard>
      ) : (
        <DataTable
          columns={columns}
          data={visible}
          onRowClick={(s) => setSelectedId(s.statement_id)}
          summary={
            <>
              <TableCell className="font-medium">Summe</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell className={cn('font-medium', num)}>{chfOrDash(sumSoll)}</TableCell>
              <TableCell className={cn('font-medium', num)}>{chfOrDash(sumIst)}</TableCell>
              <TableCell className={cn('font-medium', num)}>{signedChfOrDash(sumDelta)}</TableCell>
              <TableCell />
              <TableCell />
            </>
          }
        />
      )}

      {selected && (
        <StatementPeek
          key={selected.statement_id}
          statement={selected}
          index={selectedIndex}
          total={visible.length}
          onClose={() => setSelectedId(null)}
          onPrev={gotoPrev}
          onNext={() => {
            if (selectedIndex >= 0 && selectedIndex < visible.length - 1) gotoNext();
            else setSelectedId(null);
          }}
          onOpenPolice={(policyId) => void navigate({ to: '/policen/$id', params: { id: policyId } })}
          onToast={showFeedback}
          onError={showError}
        />
      )}

      {feedback && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 rounded-lg border border-status-success-border bg-status-success-bg px-4 py-2.5 text-sm text-status-success shadow-md"
        >
          {feedback}
        </div>
      )}
      {errorFeedback && (
        <div
          role="alert"
          className="fixed bottom-16 right-4 z-50 rounded-lg border border-status-danger-border bg-status-danger-bg px-4 py-2.5 text-sm text-status-danger shadow-md"
        >
          {errorFeedback}
        </div>
      )}
    </>
  );
}

// ── Akonto slice ─────────────────────────────────────────────────────────────

function AkontoSlice() {
  const navigate = useNavigate();
  const { data = [], isLoading } = useCourtageAkonto();

  const columns = useMemo<ColumnDef<AkontoBalanceViewDto>[]>(
    () => [
      {
        accessorKey: 'policy_number',
        header: 'Police',
        cell: ({ row }) =>
          row.original.policy_id ? (
            <EntityLink onActivate={() => void navigate({ to: '/policen/$id', params: { id: row.original.policy_id } })}>
              <span className="mono-num">{row.original.policy_number ?? '-'}</span>
            </EntityLink>
          ) : (
            <span className="mono-num">{row.original.policy_number ?? '-'}</span>
          ),
      },
      { accessorKey: 'kunde_name', header: 'Kunde', cell: ({ row }) => <span>{row.original.kunde_name ?? '-'}</span> },
      { accessorKey: 'advanced_amount', header: 'Vorschüsse', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.advanced_amount)}</div> },
      { accessorKey: 'settled_amount', header: 'Verrechnet', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.settled_amount)}</div> },
      { accessorKey: 'open_balance', header: 'Offener Saldo', cell: ({ row }) => <div className={cn(num, 'font-medium')}>{chfOrDash(row.original.open_balance)}</div> },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const st = AKONTO_STATUS_META[row.original.status];
          return <StatusDot tone={st.tone} label={st.label} />;
        },
      },
      { accessorKey: 'due_date', header: 'Frist', cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.due_date ? dateShortDe(row.original.due_date) : '-'}</span> },
    ],
    [navigate],
  );

  if (isLoading) return <EmptyCard>Lade Akonto-Salden …</EmptyCard>;
  if (data.length === 0) return <EmptyCard>Keine offenen Akonto-Salden.</EmptyCard>;
  return <DataTable columns={columns} data={data} />;
}

// ── Co-Broker slice ──────────────────────────────────────────────────────────

const SPLIT_ROLE_LABEL: Record<string, string> = { sole: 'Allein', lead: 'Lead', recipient: 'Empfänger' };
const SPLIT_CLASS_LABEL: Record<string, string> = {
  lead_underpaid: 'Lead unterbezahlt',
  lead_not_forwarded: 'Nicht weitergeleitet',
  recipient_share_under: 'Anteil zu tief',
  recipient_no_forwarding: 'Keine Weiterleitung',
  basis_difference: 'Basisdifferenz',
};

function CoBrokerSlice() {
  const { data = [], isLoading } = useCourtageSplits();

  const columns = useMemo<ColumnDef<CommissionSplitViewDto>[]>(
    () => [
      { accessorKey: 'role', header: 'Rolle', cell: ({ row }) => <span>{SPLIT_ROLE_LABEL[row.original.role] ?? row.original.role}</span> },
      { accessorKey: 'counterparty_broker_id', header: 'Gegenpartei', cell: ({ row }) => <span className="mono-num text-xs">{row.original.counterparty_broker_id.slice(0, 8)}</span> },
      { accessorKey: 'commission_kind', header: 'Provisionsart', cell: ({ row }) => <span className="text-sm text-muted-foreground">{commissionKindLabel(row.original.commission_kind)}</span> },
      { accessorKey: 'basis_amount', header: 'Basis', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.basis_amount)}</div> },
      { accessorKey: 'expected_share', header: 'Soll-Anteil', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.expected_share)}</div> },
      { accessorKey: 'actual_share', header: 'Ist-Anteil', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.actual_share)}</div> },
      { accessorKey: 'delta_chf', header: 'Differenz', cell: ({ row }) => <div className={cn(num, diffToneClass(row.original.delta_chf))}>{signedChfOrDash(row.original.delta_chf)}</div> },
      {
        accessorKey: 'split_class',
        header: 'Klasse',
        cell: ({ row }) =>
          row.original.split_class ? (
            <Badge variant="warning">{SPLIT_CLASS_LABEL[row.original.split_class] ?? row.original.split_class}</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const st = SPLIT_STATUS_META[row.original.status];
          return (
            <div className="flex items-center gap-2">
              <StatusDot tone={st.tone} label={st.label} />
              {row.original.transparency_gap && <Badge variant="warning">Transparenzlücke</Badge>}
            </div>
          );
        },
      },
    ],
    [],
  );

  if (isLoading) return <EmptyCard>Lade Co-Broker-Splits …</EmptyCard>;
  if (data.length === 0) return <EmptyCard>Keine Co-Broker-Splits.</EmptyCard>;
  return <DataTable columns={columns} data={data} />;
}

// ── Monitoring slice ─────────────────────────────────────────────────────────

/** Last 12 months as { value: 'YYYY-MM', label }. */
function recentMonths(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value, label: dt.toLocaleDateString('de-CH', { month: 'long', year: 'numeric' }) });
  }
  return out;
}

function MonitoringSlice() {
  const [month, setMonth] = useState(currentMonth());
  const period = useMemo(() => monthBounds(month), [month]);
  const { data: kz } = useCourtageKennzahlen(period);
  const { data: expectations = [] } = useCourtageExpectations(period);
  const months = useMemo(() => recentMonths(), []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <label htmlFor="monitoring-month" className="text-sm text-muted-foreground">
          Zeitraum
        </label>
        <select
          id="monitoring-month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-8 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      <KennzahlenTiles kz={kz} />

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">Erwartungen</h3>
        <ExpectationsTable expectations={expectations} />
      </section>
    </div>
  );
}

function KennzahlenTiles({ kz }: { kz: ReconciliationKennzahlenDto | undefined }) {
  if (!kz) return <EmptyCard>Lade Kennzahlen …</EmptyCard>;
  const showCobroker =
    kz.offene_cobroker_verbindlichkeiten_chf !== null || kz.anteilsabweichungen_count > 0;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      <Kpi label="Ausstehende Courtage" value={chfOrDash(kz.ausstehende_courtage_chf)} />
      <Kpi label="Offene Differenzen" value={chfOrDash(kz.offene_differenzen_chf)} sub={`${kz.offene_differenzen_count} offen`} />
      <Kpi label="Offene Akonti" value={chfOrDash(kz.offene_akonti_chf)} />
      <Kpi label="Reconciliation-Quote" value={`${kz.reconciliation_quote_pct.toFixed(1)} %`} />
      <Kpi label="Überfällige Erwartungen" value={String(kz.ueberfaellige_erwartungen)} />
      {showCobroker && (
        <Kpi
          label="Co-Broker Abweichungen"
          value={chfOrDash(kz.anteilsabweichungen_chf)}
          sub={`${kz.anteilsabweichungen_count} Splits`}
        />
      )}
    </div>
  );
}

function ExpectationsTable({ expectations }: { expectations: CommissionExpectationViewDto[] }) {
  const columns = useMemo<ColumnDef<CommissionExpectationViewDto>[]>(
    () => [
      { accessorKey: 'policy_number', header: 'Police', cell: ({ row }) => <span className="mono-num">{row.original.policy_number ?? '-'}</span> },
      {
        id: 'periode',
        header: 'Periode',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {dateShortDe(row.original.period_from)} - {dateShortDe(row.original.period_to)}
          </span>
        ),
      },
      { accessorKey: 'commission_kind', header: 'Provisionsart', cell: ({ row }) => <span className="text-sm text-muted-foreground">{commissionKindLabel(row.original.commission_kind)}</span> },
      { accessorKey: 'expected_amount', header: 'Erwartet', cell: ({ row }) => <div className={num}>{chfOrDash(row.original.expected_amount)}</div> },
      { accessorKey: 'due_date', header: 'Frist', cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.due_date ? dateShortDe(row.original.due_date) : '-'}</span> },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const st = EXPECTATION_STATUS_META[row.original.status];
          return <StatusDot tone={st.tone} label={st.label} />;
        },
      },
    ],
    [],
  );
  const [view, setView] = useState<'offen' | 'zurueckgezogen'>('offen');
  // A withdrawn expectation (outside the courtage window) is not outstanding: its own filter only.
  const withdrawn = expectations.filter((e) => e.status === 'zurueckgezogen');
  const open = expectations.filter((e) => e.status !== 'zurueckgezogen');
  const shown = view === 'offen' ? open : withdrawn;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2" data-testid="expectations-filter">
        <FilterChip label="Erwartungen" count={open.length} active={view === 'offen'} onClick={() => setView('offen')} />
        <FilterChip
          label={COMMISSION_EXPECTATION_STATUS_LABELS.zurueckgezogen}
          count={withdrawn.length}
          active={view === 'zurueckgezogen'}
          onClick={() => setView('zurueckgezogen')}
        />
      </div>
      {shown.length === 0 ? <EmptyCard>Keine Erwartungen im Zeitraum.</EmptyCard> : <DataTable columns={columns} data={shown} />}
    </div>
  );
}

// ── Small building blocks ─────────────────────────────────────────────────────

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="flex flex-col gap-0.5 p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold tabular-nums mono-num">{value}</span>
      {sub && <span className="text-xs text-muted-foreground tabular-nums">{sub}</span>}
    </Card>
  );
}

function EmptyCard({ children, role }: { children: React.ReactNode; role?: string }) {
  return (
    <div role={role} className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function KeinZugriff() {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-10 text-center">
      <div className="grid size-10 place-items-center rounded-full bg-secondary">
        <Coins className="size-5 text-muted-foreground" />
      </div>
      <div className="text-sm font-semibold">Kein Zugriff auf Courtage</div>
      <p className="max-w-sm text-sm text-muted-foreground">
        Für den Courtage-Bereich fehlt die Berechtigung <span className="mono-num">commission.operate</span>. Bitte
        wende dich an eine administrierende Person.
      </p>
    </div>
  );
}
